import { z } from 'zod';

import { type OffsetPaginationRequestQuery } from './pagination';
import { PipelineConfigurationSchema } from './pipeline-configuration';
import { PrimaryKeySchema } from './utils';

// Status enum for pipeline configuration versions
export const PipelineConfigurationVersionStatusSchema = z.enum([
  'draft',
  'current',
  'archived',
]);

export type PipelineConfigurationVersionStatus = z.infer<
  typeof PipelineConfigurationVersionStatusSchema
>;

export const PipelineConfigurationVersionSchema = z.object({
  pipelineConfigurationVersionId: PrimaryKeySchema,
  pipelineId: PrimaryKeySchema,
  configuration: PipelineConfigurationSchema,
  version: z.number(),
  status: PipelineConfigurationVersionStatusSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type PipelineConfigurationVersion = z.infer<
  typeof PipelineConfigurationVersionSchema
>;

// Schema for creating a new pipeline configuration version
export const CreatePipelineConfigurationVersionSchema =
  PipelineConfigurationVersionSchema.pick({
    pipelineId: true,
    configuration: true,
    status: true,
  });

export type CreatePipelineConfigurationVersion = z.infer<
  typeof CreatePipelineConfigurationVersionSchema
>;

// Schema for updating a pipeline configuration version
export const UpdatePipelineConfigurationVersionSchema = z.object({
  configuration: PipelineConfigurationSchema.optional(),
  status: PipelineConfigurationVersionStatusSchema.optional(),
});

export type UpdatePipelineConfigurationVersion = z.infer<
  typeof UpdatePipelineConfigurationVersionSchema
>;

export const ListPipelineConfigurationVersionsOptionsSchema = z
  .object({
    pipelineIds: PrimaryKeySchema.array(),
    pipelineConfigurationVersionIds: PrimaryKeySchema.array(),
    status: PipelineConfigurationVersionStatusSchema.optional(),
    statuses: PipelineConfigurationVersionStatusSchema.array().optional(),
    versions: z.number().array().optional(),
    teamIds: PrimaryKeySchema.array().optional(),
  })
  .partial();

export type ListPipelineConfigurationVersionsOptions = z.infer<
  typeof ListPipelineConfigurationVersionsOptionsSchema
> &
  OffsetPaginationRequestQuery<'version' | 'createdAt' | 'updatedAt'>;
